"use client";

import { useEffect, useState } from "react";
import { useMediaMatches } from "@/hooks/use-mobile";
import { projectToMap } from "./landing-network-map";

export type CardSide = "above" | "below" | "left" | "right" | "above-start" | "above-end" | "below-start" | "below-end";
export type MapPoint = readonly [number, number];

interface Stop {
  city: string;
  state: string;
  longitude: number;
  latitude: number;
  side: CardSide;
}

interface OrderSpec {
  id: string;
  title: string;
  photo: string;
  quantity: number;
  noun: string;
  unitPriceCents: number;
  miles: number;
  documents: { quote: string; order: string; bol: string };
  from: Stop;
  to: Stop;
}

export interface MapStop extends Stop {
  point: MapPoint;
}

export interface MapOrder extends Omit<OrderSpec, "from" | "to"> {
  deliveredCents: number;
  from: MapStop;
  to: MapStop;
}

export interface ActiveOrder {
  order: MapOrder;
  phase: number;
}

export const STEP_MS = 1400;
const LANE_LIFT = 0.22;
const FREIGHT_CENTS_PER_MILE = 310;
const LANE_SAMPLES = 8;
export const PHASE_COUNT = 7;
export const DELIVERED_PHASE = 5;
export const FADING_PHASE = 6;
const STAGGER_STEPS = 3;
const SETTLED_TICK = 5;

const ORDER_SPECS: OrderSpec[] = [
  {
    id: "la-chicago",
    title: "275 gal IBC totes",
    photo: "/img/blog/2026-10/what-is-an-ibc-tote-complete-guide.webp",
    quantity: 60,
    noun: "IBC totes",
    unitPriceCents: 11_800,
    miles: 2_015,
    documents: { quote: "Q-24817", order: "RPK-10392", bol: "58210-0743" },
    from: { city: "Los Angeles", state: "CA", longitude: -118.24, latitude: 34.05, side: "right" },
    to: { city: "Chicago", state: "IL", longitude: -87.63, latitude: 41.88, side: "above" },
  },
  {
    id: "houston-richmond",
    title: "55 gal steel drums",
    photo: "/img/blog/2026-10/55-gallon-drum-dimensions.webp",
    quantity: 220,
    noun: "steel drums",
    unitPriceCents: 3_200,
    miles: 1_290,
    documents: { quote: "Q-24863", order: "RPK-10418", bol: "58214-1186" },
    from: { city: "Houston", state: "TX", longitude: -95.37, latitude: 29.76, side: "left" },
    to: { city: "Richmond", state: "VA", longitude: -77.44, latitude: 37.54, side: "left" },
  },
  {
    id: "slc-minneapolis",
    title: "Double-wall gaylords",
    photo: "/img/blog/2026-10/what-is-a-gaylord-box-history-and-common-uses.webp",
    quantity: 300,
    noun: "gaylords",
    unitPriceCents: 1_400,
    miles: 1_240,
    documents: { quote: "Q-24902", order: "RPK-10441", bol: "58219-0352" },
    from: { city: "Salt Lake City", state: "UT", longitude: -111.89, latitude: 40.76, side: "above" },
    to: { city: "Minneapolis", state: "MN", longitude: -93.27, latitude: 44.98, side: "above" },
  },
  {
    id: "sacramento-seattle",
    title: "48×40 Grade A pallets",
    photo: "/img/pallets/grade-a/02c1b4e1-aba3-4442-b455-e027b97128f4.avif",
    quantity: 520,
    noun: "pallets",
    unitPriceCents: 750,
    miles: 750,
    documents: { quote: "Q-24955", order: "RPK-10467", bol: "58223-0917" },
    from: { city: "Sacramento", state: "CA", longitude: -121.49, latitude: 38.58, side: "right" },
    to: { city: "Seattle", state: "WA", longitude: -122.33, latitude: 47.61, side: "right" },
  },
  {
    id: "denver-atlanta",
    title: "48×40 plastic pallets",
    photo: "/img/blog/2026-10/plastic-pallets-buying-guide.webp",
    quantity: 180,
    noun: "plastic pallets",
    unitPriceCents: 2_800,
    miles: 1_400,
    documents: { quote: "Q-24988", order: "RPK-10483", bol: "58227-0461" },
    from: { city: "Denver", state: "CO", longitude: -104.99, latitude: 39.74, side: "below" },
    to: { city: "Atlanta", state: "GA", longitude: -84.39, latitude: 33.75, side: "below" },
  },
  {
    id: "san-diego-dallas",
    title: "Triple-wall gaylords",
    photo: "/img/blog/2026-10/gaylord-box-prep.webp",
    quantity: 240,
    noun: "gaylords",
    unitPriceCents: 1_600,
    miles: 1_360,
    documents: { quote: "Q-25014", order: "RPK-10502", bol: "58231-1270" },
    from: { city: "San Diego", state: "CA", longitude: -117.16, latitude: 32.72, side: "right" },
    to: { city: "Dallas", state: "TX", longitude: -96.8, latitude: 32.78, side: "above" },
  },
];

function toMapStop(stop: Stop): MapStop[] {
  const point = projectToMap(stop.longitude, stop.latitude);
  return point ? [{ ...stop, point }] : [];
}

export const MAP_ORDERS: MapOrder[] = ORDER_SPECS.flatMap((spec) =>
  toMapStop(spec.from).flatMap((from) =>
    toMapStop(spec.to).map((to) => ({
      ...spec,
      deliveredCents: spec.unitPriceCents * spec.quantity + spec.miles * FREIGHT_CENTS_PER_MILE,
      from,
      to,
    })),
  ),
);

const CYCLE_STEPS = MAP_ORDERS.length * STAGGER_STEPS;

function laneControl(from: MapPoint, to: MapPoint): MapPoint {
  const lift = Math.hypot(to[0] - from[0], to[1] - from[1]) * LANE_LIFT;
  return [(from[0] + to[0]) / 2, (from[1] + to[1]) / 2 - lift];
}

export function lanePath(from: MapPoint, to: MapPoint): string {
  const control = laneControl(from, to);
  return `M${from[0]} ${from[1]}Q${control[0]} ${control[1]} ${to[0]} ${to[1]}`;
}

export function lanePoints(from: MapPoint, to: MapPoint): MapPoint[] {
  const control = laneControl(from, to);
  return Array.from({ length: LANE_SAMPLES - 1 }, (_, index) => {
    const step = (index + 1) / LANE_SAMPLES;
    const rest = 1 - step;
    return [
      rest * rest * from[0] + 2 * rest * step * control[0] + step * step * to[0],
      rest * rest * from[1] + 2 * rest * step * control[1] + step * step * to[1],
    ];
  });
}

export function activeOrders(tick: number): ActiveOrder[] {
  return MAP_ORDERS.flatMap((order, index) => {
    const phase = (((tick - index * STAGGER_STEPS) % CYCLE_STEPS) + CYCLE_STEPS) % CYCLE_STEPS;
    return phase < PHASE_COUNT ? [{ order, phase }] : [];
  });
}

const UPCOMING_ORDER_COUNT = 2;

export function upcomingOrders(active: ActiveOrder[]): MapOrder[] {
  const youngest = active.reduce<ActiveOrder | undefined>(
    (chosen, candidate) => (chosen && chosen.phase <= candidate.phase ? chosen : candidate),
    undefined,
  );
  if (!youngest) return [];
  const start = MAP_ORDERS.indexOf(youngest.order);
  return Array.from({ length: UPCOMING_ORDER_COUNT }, (_, offset) => MAP_ORDERS[(start + offset + 1) % MAP_ORDERS.length]).filter(
    (order): order is MapOrder => order !== undefined,
  );
}

export function cardAnchor({ order, phase }: ActiveOrder): MapStop {
  return phase <= 1 ? order.from : order.to;
}

export function useOrderTimeline(): { tick: number; animated: boolean } {
  const prefersReducedMotion = useMediaMatches("(prefers-reduced-motion: reduce)");
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (prefersReducedMotion) return;
    const timer = window.setInterval(() => setTick((current) => current + 1), STEP_MS);
    return () => window.clearInterval(timer);
  }, [prefersReducedMotion]);
  return prefersReducedMotion ? { tick: SETTLED_TICK, animated: false } : { tick, animated: true };
}
